import { FormControlStatus } from '@angular/forms';
import { GroupedFormData, ReportFormRuleTemplate } from '@navstation-portal/reporting/data-model/ui';
import { DropboxItem, LatLngModel, ObjectId } from '@navstation-portal/shared/data-model/common';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { ReportType } from '../report-type';
import { BunkerRowEntry, BunkerState } from './bunker/bunker.state';
import { CargoDetailsState } from './cargo-details/cargo-details.state';
import { ConsumptionsState } from './consumptions/consumptions.state';
import { OperationalState } from './operational/operational.state';
import { PositionAndWeatherState } from './position-and-weather/position-and-weather.state';
import { EngineMode, PowerState } from './power/power.state';
import { ReportStatus } from './report-status';
import { StockState } from './stock/stock.state';

export type BaseStateFieldType =
  | boolean
  | number
  | string
  | Date
  | SelectOption
  | FormControlStatus
  | undefined
  | LatLngModel
  | BunkerRowEntry[]
  | EngineMode[]
  | string[]
  | ObjectId
  | DropboxItem[]
  | Record<string, string | number | SelectOption | LatLngModel | undefined>;

export type StateFieldType = BaseStateFieldType | Record<string, BaseStateFieldType>;

export interface StateField<T> {
  [key: string]: T | boolean | boolean | FormControlStatus;
  value: T;
  touched: boolean;
  pristine: boolean;
  status: FormControlStatus;
}

export type FormSectionStateArray = Record<string, StateField<StateFieldType>>[];
export type FormState = Record<string, FormSectionState>;
export type FormSectionState = Record<string, StateField<StateFieldType> | FormSectionStateArray>;

export interface CalculationStatus {
  loading: boolean;
  error?: string;
}

export interface StoreFormState {
  formState: FormState;
  calculations?: Record<string, CalculationStatus>;
}

export interface ReportState {
  readonly carryOverReport: IReport;
  readonly reportType: ReportType | null;
  readonly formFieldRules: Record<string, GroupedFormData> | null;
  readonly formRuleTemplate: ReportFormRuleTemplate | null;
  readonly notes: string;
  readonly reportNotFound: boolean;
  readonly localReportId: string | null;
  readonly savedReportId: string | null;
  readonly saving: boolean;
  readonly loading: boolean;
  readonly submitting: boolean;
  readonly deleting: boolean;
  readonly reportStatus: ReportStatus | null;
}
export interface IReport {
  readonly type: ReportType;
  readonly status?: ReportStatus;
  readonly operational: OperationalState;
  readonly positionAndWeather: PositionAndWeatherState;
  readonly power: PowerState;
  readonly cargoDetails: CargoDetailsState;
  readonly consumption: ConsumptionsState;
  readonly stock: StockState;
  readonly bunker: BunkerState;
}

export type IReportTabState =
  | OperationalState
  | PositionAndWeatherState
  | PowerState
  | CargoDetailsState
  | ConsumptionsState
  | BunkerState
  | StockState;

export const initialReportState: ReportState = {
  carryOverReport: {} as IReport,
  reportType: null,
  formFieldRules: null,
  formRuleTemplate: null,
  notes: '',
  reportNotFound: false,
  localReportId: null,
  savedReportId: null,
  saving: false,
  loading: false,
  submitting: false,
  deleting: false,
  reportStatus: null,
};

export const initialFormState: StoreFormState = {
  formState: {},
  calculations: {},
};

export const INITIAL_STATE_FIELD = {
  pristine: true,
  touched: false,
  status: 'VALID' as FormControlStatus,
} as const;

export const INIT_MODES = {
  CARRY_OVER: 'CARRY_OVER',
  IMPORT: 'IMPORT',
  BLANK: 'BLANK',
};
export type InitMode = (typeof INIT_MODES)[keyof typeof INIT_MODES];

export type AllStateskeys =
  | keyof OperationalState
  | keyof PositionAndWeatherState
  | keyof PowerState
  | keyof CargoDetailsState
  | keyof ConsumptionsState
  | keyof StockState;
